import { ReactNode, createContext, useState } from "react";
import { useAuthApi } from "../hooks/use-auth-api.ts";
import { clearDraft } from "../../functions/verificationDraft.ts";
import { ApiMethod, ApiResponseType } from "../../api/types.ts";

type ContextType = {
    isAuthenticated: boolean;
    login(contactnumber: string, password: string, totpCode: string | null);
    logout(): void;
    checkLoginStatus(): void;
    sendAuthGuardedRequest(
        method: ApiMethod,
        path: string,
        // eslint-disable-next-line
        body?: any,
        init?: RequestInit,
    ): Promise<unknown>;
};

const AuthContext = createContext<ContextType | undefined>(undefined);

function AuthProvider({ children }: { children: ReactNode }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const {
        login: authLogin,
        logout: authLogout,
        checkLoginStatus: authCheckLoginStatus,
        sendAuthGuardedRequest: authSendAuthGuardedRequest,
    } = useAuthApi();

    const checkLoginStatus = () => {
        let isValidLoggedIn = authCheckLoginStatus();
        if (isValidLoggedIn) {
            setIsAuthenticated(true);
        }
    }

    const login = async (contactnumber: string, password: string, totpCode: string | null = null) => {
        const authLoginPromise = authLogin(contactnumber, password, totpCode);
        return authLoginPromise.then(response => {
            if (response.type === ApiResponseType.OK) {
                setIsAuthenticated(true);
            } else {
                setIsAuthenticated(false);
            }
            return response;
        })
    };

    const logout = () => {
        authLogout();
        clearDraft();
        setIsAuthenticated(false);
    };

    const sendAuthGuardedRequest = async (
        method: ApiMethod,
        path: string,
        // eslint-disable-next-line
        body?: any,
        init?: RequestInit,
    ) => {
        return authSendAuthGuardedRequest(
            () => {
                setIsAuthenticated(false);
            },
            method,
            path,
            body,
            init,
        );
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                checkLoginStatus,
                login,
                logout,
                sendAuthGuardedRequest,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export { AuthProvider, AuthContext };
